/**
 * Forecast effects hooks — extraídos de ForecastPage.tsx (P7 split).
 *
 * Consolida 5 hooks pequeños:
 *   - useFetchProducts: GET /api/forecasting/product-mapping al mount.
 *   - useDebouncedValue: debounce genérico de un valor (250ms default).
 *   - useBodyOverflowLock: bloquea scroll del body mientras la página está montada.
 *   - useSearchAndSortIndex: construye búsqueda/sort indexes a partir de products.
 *   - usePageResetOnFilterChange: reset paginación al cambiar filtros.
 */

import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { ProductData, ProductsResponse } from "./types";
import { apiClient } from "../../services/apiClient";

interface UseFetchProductsOptions {
  setProducts: Dispatch<SetStateAction<ProductData[]>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

export function useFetchProducts(opts: UseFetchProductsOptions): void {
  const { setProducts, setLoading, setError } = opts;
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<ProductsResponse>(
          "/forecasting/product-mapping",
        );
        const data = response.data;
        if (data.status === "success") {
          setProducts(data.data);
        } else {
          setError("Error al cargar los productos");
        }
      } catch (err) {
        setError("Error de conexión al cargar productos");
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);
}

export function useDebouncedValue<T>(value: T, delay: number = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function useBodyOverflowLock(): void {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);
}

interface UseSearchAndSortIndexOptions {
  products: ProductData[];
  searchIndexRef: RefObject<Record<string, string>>;
  sortNameRef: RefObject<Record<string, string>>;
  getDisplayText: (
    text: string | { en_US: string; es_MX: string } | null,
  ) => string;
}

export function useSearchAndSortIndex(
  opts: UseSearchAndSortIndexOptions,
): void {
  const { products, searchIndexRef, sortNameRef, getDisplayText } = opts;
  useEffect(() => {
    const normalizeLocal = (text: string): string =>
      text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();

    const nextSearchIndex: Record<string, string> = {};
    const nextSortNameIndex: Record<string, string> = {};

    for (const p of products) {
      const displayName = getDisplayText(p.unifiedName);
      nextSortNameIndex[p.unifiedProductId] = displayName;
      const combined = `${displayName} ${p.unifiedCode || ""} ${p.unifiedBarcode || ""} ${p.unifiedCategory || ""} ${p.matchingMethod || ""} ${String(p.totalAvailableQty ?? 0)} ${String(p.locationsOnHandCount ?? 0)} ${String(p.totalSales ?? 0)}`;
      nextSearchIndex[p.unifiedProductId] = normalizeLocal(combined);
    }

    if (searchIndexRef.current) {
      Object.keys(searchIndexRef.current).forEach(
        (k) => delete searchIndexRef.current![k],
      );
      Object.assign(searchIndexRef.current, nextSearchIndex);
    }
    if (sortNameRef.current) {
      Object.keys(sortNameRef.current).forEach(
        (k) => delete sortNameRef.current![k],
      );
      Object.assign(sortNameRef.current, nextSortNameIndex);
    }
  }, [products, getDisplayText]);
}

interface UsePageResetOnFilterChangeOptions {
  setPage: Dispatch<SetStateAction<number>>;
  deps: unknown[];
}

export function usePageResetOnFilterChange(
  opts: UsePageResetOnFilterChangeOptions,
): void {
  const { setPage, deps } = opts;
  useEffect(() => {
    setPage(0);
  }, deps);
}

export function useCategoryTree(products: ProductData[]): {
  categoryList: string[];
  categoryCounts: Record<string, number>;
  unclassifiedCount: number;
} {
  const [data, setData] = useState<{
    categoryList: string[];
    categoryCounts: Record<string, number>;
    unclassifiedCount: number;
  }>({ categoryList: [], categoryCounts: {}, unclassifiedCount: 0 });

  useEffect(() => {
    const list = Array.from(
      new Set(
        products
          .filter((p) => p.unifiedCategory && p.unifiedCategory.trim())
          .map((p) => p.unifiedCategory!),
      ),
    ).sort();

    const counts: Record<string, number> = {};
    let unclass = 0;
    for (const p of products) {
      if (p.unifiedCategory && p.unifiedCategory.trim()) {
        counts[p.unifiedCategory] = (counts[p.unifiedCategory] || 0) + 1;
      } else {
        unclass++;
      }
    }
    setData({ categoryList: list, categoryCounts: counts, unclassifiedCount: unclass });
  }, [products]);

  return data;
}
